import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { location as getLocation } from '@app/selectors/routing';
import { trackHomeWidgetLoaded } from './analytics';

/**
 * Tracks widget-level HOME_WIDGET_LOADED events on the Home page.
 *
 * - Fires **once** per widget per Home page visit when the widget
 *   completes its initial data load.
 * - Does **not** re-fire on subsequent data refreshes, polling,
 *   pagination, or widget re-renders.
 * - Uses a WeakMap keyed on the routing location object so tracking
 *   state is automatically garbage-collected when the user navigates
 *   away, and fresh tracking begins on the next Home visit.
 */
const trackedObjectSessions = new WeakMap<object, Set<string>>();
const trackedPrimitiveSessions = new Map<
  string | number | boolean | symbol,
  Set<string>
>();

const getSessionWidgets = (key: unknown): Set<string> | undefined => {
  if (typeof key === 'object' && key !== null) {
    return trackedObjectSessions.get(key);
  }
  if (
    typeof key === 'string' ||
    typeof key === 'number' ||
    typeof key === 'boolean' ||
    typeof key === 'symbol'
  ) {
    return trackedPrimitiveSessions.get(key);
  }
  return undefined;
};

const setSessionWidgets = (key: unknown, widgets: Set<string>): void => {
  if (typeof key === 'object' && key !== null) {
    trackedObjectSessions.set(key, widgets);
  } else if (
    typeof key === 'string' ||
    typeof key === 'number' ||
    typeof key === 'boolean' ||
    typeof key === 'symbol'
  ) {
    trackedPrimitiveSessions.set(key, widgets);
  }
};

const useTrackHomeWidgetLoaded = (
  widgetName: string,
  isLoaded: boolean,
  count: number
) => {
  const routingLocation = useSelector(getLocation);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || hasFiredRef.current || routingLocation == null) {
      return;
    }

    let sessionWidgets = getSessionWidgets(routingLocation);
    if (!sessionWidgets) {
      sessionWidgets = new Set();
      setSessionWidgets(routingLocation, sessionWidgets);
    }

    if (sessionWidgets.has(widgetName)) {
      hasFiredRef.current = true;
      return;
    }

    hasFiredRef.current = true;
    sessionWidgets.add(widgetName);
    trackHomeWidgetLoaded(widgetName, count);
  }, [isLoaded, widgetName, count, routingLocation]);
};

export default useTrackHomeWidgetLoaded;
